/**
 * This module contains a very simple state function for use with rxjs to allow a reactive local state to be used.
 *

 * @module simple-state
 */

import { Observable, scan, shareReplay, Subject, tap } from 'rxjs';

export type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

type SetState<T> = {
    type: 'SET';
    data: T;
};

interface PatchState<T> {
    type: 'PATCH';
    data: DeepPartial<T>;
}

interface MutateState<T> {
    type: 'MUTATE';
    fn: (s: T) => T;
}

/**
 * Replace the current state with the new state.
 *
 * @param data the new data for the state
 * @returns set state command
 */
export function setState<T>(data: T): SetState<T> {
    return {
        type: 'SET',
        data
    };
}

/**
 * Patch the state with this update. Any `undefined` values are ignore.
 * Use `null` to clear a value. This allows for simpler update usage.
 *
 * @param data the data to patch with
 * @returns patch state command
 */
export function patchState<T>(data: DeepPartial<T>): PatchState<T> {
    return {
        type: 'PATCH',
        data
    };
}

/**
 * Run the mutator function on the state.
 *
 * @param fn the mutator function to run which returns the new state
 * @returns mutate state command
 */
export function mutateState<T>(fn: (s: T) => T): MutateState<T> {
    return {
        type: 'MUTATE',
        fn
    };
}

export type StateUpdateCommand<T> =
    | SetState<T>
    | PatchState<T>
    | MutateState<T>;

/**
 * Recursively merges source into target, ignoring undefined values.
 * Creates new objects to maintain immutability.
 *
 * @param target the target object to merge into
 * @param source the source object to merge from
 * @returns the merged result
 */
function deepMerge<T>(target: T, source: DeepPartial<T>): T {
    // Handle non-object types
    if (
        source === null ||
        typeof source !== 'object' ||
        Array.isArray(source)
    ) {
        return source as T;
    }

    if (
        target === null ||
        typeof target !== 'object' ||
        Array.isArray(target)
    ) {
        return source as T;
    }

    const result = { ...target } as T;

    for (const key in source) {
        if (source.hasOwnProperty(key)) {
            const sourceValue = source[key];

            // Skip undefined values
            if (sourceValue === undefined) {
                continue;
            }

            // If source value is null, set it directly (allows clearing)
            if (sourceValue === null) {
                result[key] = null;
                continue;
            }

            // If source value is an object and target has the same key as object, recurse
            if (isPlainObject(sourceValue) && isPlainObject(result[key])) {
                result[key] = deepMerge(result[key], sourceValue);
            } else {
                // Otherwise, directly assign (handles primitives, arrays, and new nested objects)
                result[key as keyof T] = sourceValue as T[keyof T];
            }
        }
    }

    return result;
}

function isPlainObject(value: unknown): value is Record<string, any> {
    if (value === null || typeof value !== 'object') return false;
    return (
        Object.getPrototypeOf(value) === Object.prototype ||
        Object.getPrototypeOf(value) === null
    ); // handles Object.create(null)
}

function swallowOutput(...data: any[]): void {}

/**
 * Convert a command source into a patchable state observable.
 *
 * @param commandSource the command source
 * @param initialState the initial state of the source
 * @param commandStreamPrinter function used to print command stream (e.g., console.log)
 * @returns
 */
export function patchableState<T>(
    commandSource: Subject<StateUpdateCommand<T>>,
    initialState: T,
    commandStreamPrinter = swallowOutput
): Observable<T> {
    return commandSource.pipe(
        tap(commandStreamPrinter),
        scan(
            (acc, command) => patchableStateReducer(acc, command),
            initialState
        ),
        shareReplay(1)
    );
}

/**
 * A utility function for use with rxjs `scan` for making a patchable state. This will require
 * a way to share the state with all the subscribers. e.g.,
 *
 * ```typescript
 *     private lockedFieldsSource = new ReplaySubject<
 *      StateUpdateCommand<LockedMissionFields>
 *  >(1);
 *  lockedFields$ = this.lockedFieldsSource.pipe<LockedMissionFields>(
 *      scan((acc, command) => patchableState(acc, command), {
 *          aircraft: false,
 *          isDummy: false,
 *          missionCrew: false,
 *          missionType: false,
 *          missionWorkflowVersion: false,
 *          timeZone: false
 *      }),
 *      shareReplay(1)
 *  );
 * ```
 *
 * @param acc the accumulated value
 * @param value the patch value
 * @returns the updated state
 */

export function patchableStateReducer<T>(
    acc: T,
    command: StateUpdateCommand<T>
): T {
    if (command.type === 'SET') {
        return command.data;
    }
    if (command.type === 'PATCH') {
        return deepMerge(acc, command.data);
    }
    if (command.type === 'MUTATE') {
        return command.fn(acc);
    }
}
